import './AccessStatistics.scss';

import { AccessStatisticsLegend } from './AccessStatisticsLegend';
import { AccessStatisticsRow } from './AccessStatisticsRow';
import { AccessStatisticsSection } from './AccessStatisticsSection';
import { ApplicationAccessStat } from './ApplicationAccessStat';
import { createAsyncSelectors } from '@lib/selectors';
import { DATA_SECURITY_DASHBOARD } from '@sonar-software/sonar-ts-constants';
import { DataSecurityViewQueryParams } from '@security/DataSecurityView';
import { FeatureBoundary } from '@ui/features';
import { getPastDate } from '@sonar-software/sonar-ts-lib';
import { SonarCard } from '@ui/general';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { When } from 'react-if';

import {
	coreActions,
	DATA_SENSITIVITY_COLORS,
	DataSensitivityLevelId,
	FeatureFlag
} from '@sonar-software/sonar-core';
import React, { useEffect, useMemo } from 'react';
import { useAction, useAsyncState, useDataSecurityAppColors, usePrevious } from '@hooks';

const getMaxRequestCount = (items?: {requestCount: number}[]) =>
	items?.reduce((acc, cur) => Math.max(acc, cur.requestCount), 0) ?? 0;

export const getDataSecurityAccessStatisticsSelector =
	createAsyncSelectors((state) => state.core.dataSecurity.accessStatistics.get);

export interface AccessStatisticsProps{
	applicationId?: number;
	viewParams?: DataSecurityViewQueryParams;
}

export const AccessStatistics : React.FC<AccessStatisticsProps> = ({ applicationId, viewParams }) => {
	const previousDateRange = usePrevious(viewParams?.dateRange);
	const previousAppGroup = usePrevious(viewParams?.appGroup);
	const { data, isRequested } = useAsyncState(getDataSecurityAccessStatisticsSelector);
	const applicationColors = useDataSecurityAppColors(data?.applications?.map((app) => app.applicationId));

	const getDataSecurityAccessStatistics = useAction(coreActions.requestGetCoreDataSecurityAccessStatistics);

	const hasFieldClassifications = useMemo(() => !data?.fields.every(
		(field) => field.dataSensitivityLevelId === DataSensitivityLevelId.None
	), [ data?.fields ]);

	const classifications = useMemo(() => data?.fields.reduce(
		(counts, field) => ({
			...counts,
			[ field.dataSensitivityLevelId ]: true
		}),
		{} as Record<DataSensitivityLevelId, boolean>
	), [ data?.fields ]);

	useEffect(() => {
		if (
			!viewParams?.dateRange.start ||
			viewParams?.dateRange.end !== previousDateRange?.end ||
			viewParams?.dateRange.start !== previousDateRange?.start ||
		    (!applicationId && viewParams?.appGroup !== previousAppGroup)
		) {
			getDataSecurityAccessStatistics({
				applicationId,
				end: viewParams?.dateRange.end,
				stakeholderGroupId: viewParams?.appGroup,
				start: viewParams?.dateRange.start ?? getPastDate(7)
			});
		}
	}, [ applicationId, viewParams, getDataSecurityAccessStatistics, previousDateRange, previousAppGroup ]);

	return (
		<FeatureBoundary feature={ FeatureFlag.DataSecurityDashboard }>
			<SonarCard
				className='access-statistics'
				loading={ isRequested }
				title={ DATA_SECURITY_DASHBOARD.ACCESS_STATISTICS }
				titleClassName='h2'
				useSpinnerLoading>
				<div className='access-statistics-table'>
					<AccessStatisticsSection
						isEmpty={ !data?.fields.length }
						section='fields'>
						{
							data?.fields.map((field) =>
								<AccessStatisticsRow
									colorName={
										hasFieldClassifications ?
											DATA_SENSITIVITY_COLORS[ field.dataSensitivityLevelId ] : 'blue'
									}
									count={ field.requestCount }
									id={ field.sonarObjectId }
									key={ field.sonarObjectId }
									max={ getMaxRequestCount(data.fields) }
									name={ field.fieldName }
									type={ SonarObjectType.SalesforceField } />)
						}
						<When condition={ hasFieldClassifications }>
							<AccessStatisticsLegend classifications={ classifications } />
						</When>
					</AccessStatisticsSection>
					<AccessStatisticsSection
						isEmpty={ !data?.entities.length }
						section='entities'>
						{
							data?.entities.map((entity) =>
								<AccessStatisticsRow
									colorName='blue'
									count={ entity.requestCount }
									id={ entity.sonarObjectId }
									key={ entity.sonarObjectId }
									max={ getMaxRequestCount(data.entities) }
									name={ entity.entityName }
									type={ SonarObjectType.SalesforceEntity } />)
						}
					</AccessStatisticsSection>
					<AccessStatisticsSection
						hide={ Boolean(applicationId) }
						isEmpty={ !data?.applications?.length }
						section='applications'>
						{
							data?.applications?.map((application) =>
								<ApplicationAccessStat
									color={ applicationColors[ application.applicationId ] ?? '' }
									count={ application.requestCount }
									id={ application.applicationId }
									key={ application.applicationId }
									max={ getMaxRequestCount(data?.applications) } />)
						}
					</AccessStatisticsSection>
				</div>
			</SonarCard>
		</FeatureBoundary>
	);
};
